
import React, { lazy, Suspense, useMemo, useState } from 'react'; // Added useState
import { SectionContainer } from './sections';
import { CheckoutSidebar } from './sections';
import { useFirebase } from '@/context/FirebaseProvider';
import { GoCampaignProvider } from '@/context/GoCampaignContext'; // Import GoCampaignProvider
import { LocationContextProvider } from '@/context/LocationContext'; // Import LocationContextProvider
import { LeadDeliveryStateProvider } from '@/context/leadDelivery'; // Import LeadDeliveryStateProvider

// Properly type the lazy-loaded components using correct module structure
const MainSections = lazy(() => import('./sections/main-sections/MainSections').then(
  module => ({ default: module.default })
));

// PromotionStateProvider and LeadDeliveryStateProvider will be used by MainSections internally
// if they are still needed, or their logic will be integrated/managed via GoCampaignContext.
// For now, we assume MainSections will handle their instantiation if necessary,
// or that GoCampaignContext provides enough for them.

interface ManageGoCampaignStepProps {
  businessId: string;
  onComplete: () => void;
}

// Loading fallback component
const LoadingFallback = () => (
  <div className="w-full h-32 flex items-center justify-center">
    <div className="animate-pulse space-y-2 text-center">
      <div className="h-4 w-32 bg-gray-200 rounded mx-auto"></div>
      <p className="text-sm text-gray-500">Loading section...</p>
    </div>
  </div>
);

/**
 * Component that provides all necessary context providers
 * in a more maintainable structure using GoCampaignContext
 */
const ManageGoCampaignStepRefactored: React.FC<ManageGoCampaignStepProps> = ({
  businessId,
  onComplete
}) => {
  const { businesses: allBusinesses } = useFirebase();
  const business = useMemo(() =>
    (allBusinesses || []).find(b => b.id === businessId),
    [allBusinesses, businessId]
  );
  const industry = business?.industry || '';

  // Get locationSettings from GoCampaignContext to determine activeTargetType
  // This assumes GoCampaignContext provides locationSettings which includes targetType
  // If not, targetType state needs to be managed here or passed differently.
  // For now, let's assume GoCampaignContext will hold the persisted targetType.
  // We'll need a local state for UI interaction that syncs with context.
  
  // This component itself doesn't directly manage targetType for LocationContext.
  // LocationSectionWithGoCampaign manages its own internal targetType for UI.
  // LocationContextProvider will receive that targetType from LocationSectionWithGoCampaign
  // if we decide to pass it up, or LocationContext can have its own internal targetType state
  // that gets updated by components within it.

  // For now, LocationContextProvider expects an activeTargetType prop.
  // LocationSectionWithGoCampaign has its own `targetType` state.
  // To make this work, `activeTargetType` for LocationContextProvider should reflect
  // the `targetType` from `LocationSectionWithGoCampaign`. This suggests either:
  // 1. Lifting `targetType` state from `LocationSectionWithGoCampaign` to here.
  // 2. `LocationContextProvider` itself managing `activeTargetType` and providing a setter.

  // Let's try lifting the state for `targetType` here.
  // This means `LocationSectionWithGoCampaign` will receive `targetType` and `setTargetType` as props.
  const [uiActiveTargetType, setUiActiveTargetType] = useState<'radius' | 'state' | 'all_us' | null>('state');


  return (
    <GoCampaignProvider businessId={businessId}>
      <LocationContextProvider currentBusiness={business || null} activeTargetType={uiActiveTargetType}>
        <LeadDeliveryStateProvider businessId={businessId}> {/* Wrap with LeadDeliveryStateProvider */}
          <Suspense fallback={<LoadingFallback />}>
            <SectionContainer>
              <Suspense fallback={<LoadingFallback />}>
                <MainSections 
                  businessId={businessId} 
                  onComplete={onComplete}
                  uiActiveTargetType={uiActiveTargetType}
                  setUiActiveTargetType={setUiActiveTargetType}
                />
              </Suspense>
              <CheckoutSidebar 
                businessId={businessId} 
                onComplete={onComplete} 
              />
            </SectionContainer>
          </Suspense>
        </LeadDeliveryStateProvider>
      </LocationContextProvider>
    </GoCampaignProvider>
  );
};

export default ManageGoCampaignStepRefactored;
